'use client';

import { Dialog } from '@base-ui-components/react/dialog';
import { useTranslation } from 'next-i18next';
import { useState } from 'react';

import type { Contact } from '@/types/service-api';

interface ContactSelectDialogProps {
  open: boolean;
  contacts: Contact[];
  onSelectContact: (contact: Contact) => void;
  onClose: () => void;
  onCustomContact: () => void;
}

export function ContactSelectDialog({
  open,
  contacts,
  onSelectContact,
  onClose,
  onCustomContact,
}: ContactSelectDialogProps) {
  const { t } = useTranslation('common');
  const [selectedIndex, setSelectedIndex] = useState<number | null>(null);

  const handleSelect = () => {
    if (selectedIndex !== null && contacts[selectedIndex]) {
      onSelectContact(contacts[selectedIndex]);
      onClose();
    }
  };

  const handleCustomContact = () => {
    onCustomContact();
    onClose();
  };

  return (
    <Dialog.Root open={open} onOpenChange={(newOpen) => !newOpen && onClose()}>
      <Dialog.Portal>
        <Dialog.Backdrop className="fixed inset-0 z-50 bg-black/50" />
        <Dialog.Popup className="fixed left-1/2 top-1/2 z-50 w-full max-w-2xl -translate-x-1/2 -translate-y-1/2 rounded-lg bg-white p-6 shadow-xl">
          <div className="mb-4 flex items-center justify-between">
            <Dialog.Title className="text-h3Mobile font-bold lg:text-h3Desktop">
              {t('complaint.contact_dialog.title')}
            </Dialog.Title>
            <Dialog.Close
              className="rounded-full p-2 hover:bg-gray-100"
              onClick={onClose}
            >
              <svg
                className="size-6"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </Dialog.Close>
          </div>

          <Dialog.Description className="mb-6 text-p1CopyMobile text-gray-600 lg:text-p1CopyDesktop">
            {t('complaint.contact_dialog.description')}
          </Dialog.Description>

          {contacts.length === 0 ? (
            <div className="mb-6 rounded-lg bg-gray-50 p-8 text-center">
              <p className="text-p1CopyMobile text-gray-600 lg:text-p1CopyDesktop">
                {t('complaint.contact_dialog.no_contacts')}
              </p>
            </div>
          ) : (
            <div className="mb-6 max-h-96 space-y-2 overflow-y-auto">
              {contacts.map((contact, index) => (
                <button
                  key={index}
                  type="button"
                  onClick={() => setSelectedIndex(index)}
                  className={`w-full rounded-lg border-2 p-4 text-left transition-colors ${
                    selectedIndex === index
                      ? 'border-black bg-gray-50'
                      : 'border-gray-300 hover:border-gray-400'
                  }`}
                >
                  <div className="text-p1CopyMobile font-semibold lg:text-p1CopyDesktop">
                    {contact.name}
                  </div>
                  {contact.telNr && (
                    <div className="mt-1 text-p2CopyMobile text-gray-600 lg:text-p2CopyDesktop">
                      {t('complaint.fields.phone')}: {contact.telNr}
                    </div>
                  )}
                  {contact.mobileNr && (
                    <div className="text-p2CopyMobile text-gray-600 lg:text-p2CopyDesktop">
                      {t('complaint.fields.mobile')}: {contact.mobileNr}
                    </div>
                  )}
                  {contact.mail && (
                    <div className="text-p2CopyMobile text-gray-600 lg:text-p2CopyDesktop">
                      {t('complaint.fields.email')}: {contact.mail}
                    </div>
                  )}
                </button>
              ))}
            </div>
          )}

          <div className="flex gap-4">
            <button
              type="button"
              onClick={handleSelect}
              disabled={selectedIndex === null}
              className="flex-1 border border-black bg-black px-6 py-3 text-p1CopyMobile uppercase text-white transition-colors hover:bg-gray-800 disabled:cursor-not-allowed disabled:bg-gray-400 disabled:opacity-50 lg:text-p1CopyDesktop"
            >
              {t('complaint.buttons.select')}
            </button>
            <button
              type="button"
              onClick={handleCustomContact}
              className="flex-1 border border-black bg-white px-6 py-3 text-p1CopyMobile uppercase text-black transition-colors hover:bg-gray-100 lg:text-p1CopyDesktop"
            >
              {t('complaint.contact_dialog.own_contact')}
            </button>
          </div>
        </Dialog.Popup>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
